/**
 * Tracker class for event tracking, consent management, and analytics integration.
 *
 * The Tracker class manages all MikMak tracking, including:
 * - Sending events to analytics endpoints (Cloudfront, Datadog, custom tracking hosts, etc.)
 * - Integrating with consent management platforms (Ketch, GTM-based, or custom)
 * - Handling Google Tag Manager (GTM) event construction, queuing, and payload mapping
 * - Processing server- and client-defined tracking actions
 * - Managing consent states, event queuing, and (optionally) LiveRamp pixel drops
 * - Augmenting tracking events with data from the player and contextual event payloads
 *
 * This class is instantiated as a singleton accessible via the window as `window.tracker`.
 *
 * @example
 * // Track a pageview
 * tracker.track('pv', { auto: true }, TARGET_ALL)
 *
 * @example
 * // Track a buy event
 * tracker.track('buy', { productId: '123' }, TARGET_TRK)
 *
 * @example
 * // Set consent via Ketch or custom CMP
 * tracker.setConsent(true, false)
 */
class Tracker {
    constructor() {
        /** Flag indicating whether the GTM script has been appended to the document */
        this.gtmScriptAppended = false;
        this.KETCH_CODES = {
            ADVERTISING: 'behavioral_advertising',
        };
        this.CUSTOM_CONSENT_CODE = '__custom';
        // the consent depends on the Consent Management Platform (CMP) used (Ketch by default, or custom in GTM container)
        this.CONSENT_CODE = this.KETCH_CODES.ADVERTISING;
        if (player.data.gtmId && player.data.useMediaExternalConsent)
            this.CONSENT_CODE = this.CUSTOM_CONSENT_CODE;
        this.hasDroppedLiveRampPixel = false;
        if (player.data.gtmId) {
            this.gtmIdPresent = true;
            if (player.data.useMediaExternalConsent)
                this._loadGTM(player.data.gtmId);
        }
        // decode tracking plact actions' url/script values
        if (player.data.trkActions || player.data.gtmId || player.data.isLiveRampEnabled) {
            const trackingActions = player.data.trkActions || [];
            trackingActions.forEach((a) => {
                try {
                    a.value = decodeURIComponent(a.value);
                }
                catch (err) {
                    // Handle malformed percent-encoding gracefully (e.g., %ep, %epid, %ecid)
                    logger.error(`Malformed URL component [value=${a.value}]`, err);
                    // Keep original value if decoding fails
                }
            });
            // stores trackers' state
            this.consentMap = this._buildConsentMap(trackingActions, player.data.gtmId, player.data.isLiveRampEnabled);
        }
        this._seen = new Set(); // events seen
        this.consentLevel = -1; // 0: required cookie rejected, 1: required cookie accepted
        this.consent_values = null;
    }
    /**
     * Drops pixels and runs custom tracking from tracking plan
     *
     * This method handles event tracking by sending data to various endpoints based on the specified targets.
     * It supports sending to tracking hosts, datadog, or both depending on the targets bitmask.
     *
     * @param action - The event name to track
     * @param data - Additional information object for the tracking event
     * @param targets - Bitmask specifying tracking targets:
     *   - 1: send to trk host
     *   - 2: send to datadog
     *   - 3: send to both
     * @returns void
     *
     * @example
     * ```typescript
     * tracker.track('buy', { productId: '123', price: 29.99 }, TARGET_TRK);
     * tracker.track('error', { message: 'API failed' }, TARGET_LOG);
     * ```
     */
    track(action, data = {}, targets = TARGET_TRK) {
        try {
            performance.measure(`trk-ts-${action}`, 'origin');
            const payload = this._getTrackingParam(action, data); // get parameters object
            const { rfr, rfr2, ...qsParams } = payload;
            // tech log is not for custom tracking
            // only perform client-defined tracking, if a gtmId is not present
            if (!data.log) {
                if (this.gtmIdPresent) {
                    this._gtmTracking(action, data);
                }
                else {
                    this._customTracking(action, data);
                }
            }
            let queryString = Object.entries(qsParams)
                // remove empty/null/undefined values
                .filter(([_k, v]) => v != null && v !== '')
                .map(([k, v]) => `${k}=${v}`).join('&'); // and convert it to a query string
            if (rfr2)
                queryString += `&rfr2=${rfr2}`;
            if (rfr)
                queryString += `&rfr=${rfr}`;
            // only events marked as log are sent to datadog.
            if (data.log || targets & TARGET_LOG) {
                const url = `https://trace.swaven.com/log/${action}?${queryString}`;
                if (navigator.sendBeacon)
                    navigator.sendBeacon(url);
                else
                    _addPixel(url);
            }
            // others are our core analytics data, sent to cloudfront.
            if (targets & TARGET_TRK) {
                if (navigator.sendBeacon && action === 'buy')
                    navigator.sendBeacon(`${player.data.trkHost}/post?action=${action}&${queryString}&beacon=1`);
                // pixel tracking for all actions. 'buy' renamed to 'buypx'
                const pxAction = action.replace('buy', 'buypx');
                _addPixel(`${player.data.trkHost}/px.png?action=${pxAction}&${queryString}`);
            }
        }
        catch (ex) {
            console.error('Tracking error !', ex); // eslint-disable-line no-console
        }
    }
    /**
     * Accepts or refuses Ketch consent based on the provided consent object.
     *
     * This method processes the consent object returned from Ketch and sets the tracking
     * consent accordingly. Only advertising consent is considered for the Ketch consent
     * implementation; other types of consent are ignored.
     *
     * @param consentObj - The consent object returned from Ketch containing purposes and vendors
     * @param consentObj.purposes - Object mapping purpose names to boolean consent values
     * @param consentObj.vendors - Array of vendor information
     * @param auto - Whether this is the initial consent event that would be triggered automatically
     * @returns void
     *
     * @example
     * ```typescript
     * tracker.setKetchConsent({
     *   purposes: { advertising: true, analytics: false },
     *   vendors: []
     * }, false);
     * ```
     */
    setKetchConsent(consentObj, auto) {
        // only consent if adversiting, other types of consent are ignored for Ketch consent implementation
        const accepted = !!consentObj.purposes[this.KETCH_CODES.ADVERTISING];
        this.setConsent(accepted, auto);
    }
    /**
     * Accepts or refuses tracking consent and processes queued tracking events.
     *
     * This method sets the tracking consent state and processes any queued tracking events
     * that were waiting for consent. It also conditionally loads GTM and LiveRamp tracking
     * based on consent and configuration.
     *
     * @param accepted - Whether tracking is accepted by the user
     * @param auto - Whether this is an automatic or manual event
     * @returns void
     *
     * @example
     * ```typescript
     * tracker.setConsent(true, false); // User manually accepted tracking
     * tracker.setConsent(false, true); // User refused initial auto-consent
     * ```
     */
    setConsent(accepted, auto) {
        const data = this.consentMap && this.consentMap.get(this.CONSENT_CODE);
        if (!data) {
            const msg = `No data for purpose ${this.CONSENT_CODE} in consentMap`;
            console.error(msg); // eslint-disable-line no-console
            return;
        }
        data.accepted = !!accepted;
        this.consentLevel = +data.accepted;
        this.consent_values = `${this.CONSENT_CODE}=${+data.accepted}`;
        this.track('consent', { consent_value: +data.accepted, consent_name: this.CONSENT_CODE, auto: +!!auto });
        if (data.accepted && data.queue.length) {
            while (data.queue.length) {
                if (this.gtmIdPresent) {
                    this._sendGTMEvent(data.queue.shift());
                }
                else {
                    this._dropServerTracking(...data.queue.shift());
                }
            }
        }
        // only add the GTM container if the user has accepted required cookies
        // AND a GTM id exists AND the experience has not opted out of GTM tracking
        if (data.accepted && player.data.gtmId) {
            this._loadGTM(player.data.gtmId);
        }
        // only add the LiveRamp pixel if the user has accepted required cookies
        // AND the subaccount has LiveRamp enabled
        if (data.accepted
            && player.data.isLiveRampEnabled
            && !this.hasDroppedLiveRampPixel) {
            this._dropLiveRampPixel();
        }
    }
    /**
     * Builds internal tracking payload for event tracking.
     * @param action - The event name to track
     * @param payload - Additional data object for the tracking event
     * @returns The formatted tracking parameters object
     */
    _getTrackingParam(action, payload = {}) {
        // extract payload data we don't need as-is in tracking
        const { avail, ...data } = payload;
        const ts = performance.getEntriesByName(`trk-ts-${action}`, 'measure').pop()?.duration;
        const params = Object.assign({}, player.baseTrk, data, {
            ct_context: player.store && player.store.location.country || player.data.country,
            v: Date.now(),
            ts: ts ? Math.round(ts) : undefined,
        });
        if (params.auto != null)
            params.auto = +!!params.auto;
        if (params.outbound != null)
            params.outbound = +!!params.outbound;
        if (player.trkData?.avgpc != null)
            params.avgpc = player.trkData.avgpc;
        if (player.trkData?.avgdst != null)
            params.avgdist = player.trkData.avgdst;
        if (player.trkData?.nbres != null)
            params.nbres = player.trkData.nbres;
        if (player.trkData?.nbrefret != null)
            params.nbrefret = player.trkData.nbrefret;
        if (player.trkData?.avreqid)
            params.avreqid = player.trkData.avreqid;
        if (player.data?.abTestScenario)
            params.AbTestScenario = player.data.abTestScenario;
        // if (app.error === 'UNAVAILABLE_COUNTRY_ERROR')
        //   params.oos = 1
        const availParams = this._getAvailParams(avail);
        if (action !== 'pv' && action !== 'dploy' && player._store.product)
            params.pid = !player.data.isBundle
                ? player._store.product.ean
                // if bundle, send all products eans sorted by ascending ean
                : player.data.products.map((x) => x.ean)
                    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                    .join(',');
        // Add geo regional information
        if (player.store?.location) {
            params.uloc_city = player.store.location.city;
            params.uloc_country = player.store.location.country;
            params.uloc_region = player.store.location.region;
            params.uloc_latitude = Math.round(player.store.location.lat * 100) / 100;
            params.uloc_longitude = Math.round(player.store.location.lng * 100) / 100;
            params.uloc_source = player.store.location.origin;
            params.uloc_zip_code = player.store.location.zipCode;
            // track when location is the API default
            if (player.store.location.isDefault) {
                params.uloc_isDefault = 1;
            }
        }
        switch (action) {
            case 'buy':
                if (avail) {
                    if (avail.clkUrlAffiliate)
                        params.clkurlaff_prgid = avail.retailer.affiliatePrgId;
                    params.clkurlaff = +!!avail.clkUrlAffiliate;
                    params.clkurlt = avail.clkUrlType;
                }
                break;
            //   case 'geoloc':
            //     params.auto = 0 // geoloc event is always triggered by a user action
            //     break
            case 'clkloc':
                params.clkloc_type = data.clkloc_type || 'map';
                break;
            case 'clk':
                params.autoSel = +!!data.autoSel;
                break;
            case 'apires':
                params.sids = player._store.avails.slice(0, 20).map((x) => x.store.id);
                params.auto = 1;
                break;
            //   case 'close':
            //     params.auto = +!!data.auto
            //     break
            case 'unavlbsids':
                params.auto = 1;
                break;
            case 'clicktag':
                params.url = data.url;
                params.trigger = data.trigger;
                params.swavenDomain = +(/\/\/[^/]+?\bswaven\b/i.test(data.url));
                break;
        }
        if (availParams && (action === 'buy' || action === 'appointment' || action === 'clkloc')) {
            Object.entries(availParams).forEach(([k, v]) => params[k] = v);
        }
        // init params only send when necessary
        if (['apires', 'api-avails', 'storeDisplayed'].includes(action)) {
            params.init = +!this._seen.has(action);
            if (params.init)
                this._seen.add(action);
        }
        // consent action is sent for each tracker. So we cannot send an accurate consent status
        // on such action, as it may be incomplete. Just don't add the params for the action.
        if (action !== 'consent' && this.consentLevel !== -1) {
            params.consent = this.consentLevel;
            params.consent_values = this.consent_values;
        }
        if (player.data.hasVouchers && player._store.avails.length > 0)
            params.has_voucher = +player._store.avails.some((x) => x.voucher);
        const isVchrEvt = ['buy', 'clkloc', 'appointment', 'view_voucher', 'copy_voucher'].includes(action)
            || action.startsWith('form_');
        // track voucher details for relevant actions
        if (player.data.hasVouchers
            && player._store.currentAvail && player._store.currentAvail.voucher
            && isVchrEvt) {
            params.voucher_id = player._store.currentAvail.voucher.id;
            params.voucher_version_id = player._store.currentAvail.voucher.versionId;
            if (player._store.currentAvail.voucherNewPrice)
                params.voucher_new_price = player._store.currentAvail.voucherNewPrice;
        }
        if (payload instanceof Error) {
            params.msg = encodeURIComponent(payload.message);
            params.stack = encodeURIComponent(payload.stack || '');
        }
        return params;
    }
    /**
     * Extracts key tracking parameters from an availability.
     *
     * This method processes an availability object and extracts the essential tracking
     * parameters needed for analytics and tracking events. It includes pricing, distance,
     * delivery information, store details, and product identification.
     *
     * @param avail - The availability object containing store, product, and pricing information
     * @returns AvailTrackingParams object with tracking parameters, or null if avail is falsy
     */
    _getAvailParams(avail) {
        if (!avail)
            return null;
        const params = {
            prc: avail.price.amount,
            dist: avail.store.distance,
            dlv: this._getDlvParam(avail.store),
            sid: avail.store.id,
            sids: player._store.avails.slice(0, 20).map((x) => x.store.id),
            avpid: player.data.isBundle
                ? avail.bundleProducts.filter((x) => x.available).map((x) => x.ean)
                : avail.product.ean,
        };
        return params;
    }
    /**
     * Handles GTM tracking for specific actions.
     *
     * This method processes tracking events and determines whether to send them directly
     * or queue them for later processing based on consent status.
     *
     * @param action - The event name to track
     * @param data - Additional information object for the tracking event
     * @returns void
     */
    _gtmTracking(action, data) {
        // if the action does not have a corresponding GTM event
        // or it is the clk event generated on load
        // return early
        const payloads = this._structureGTMEventPayload(action, data);
        if (!payloads?.length)
            return;
        const gtmTrackingAllowed = this.gtmScriptAppended
            && this.consentMap?.get(this.CONSENT_CODE)?.accepted;
        if (gtmTrackingAllowed) {
            // if the gtm container has been added to the page, we know the user gave permission
            // so we can directly send the event
            payloads.forEach(this._sendGTMEvent);
        }
        else {
            // if a gtmId is present and the action has a corresponding gtm event, BUT the container
            // has not been appended yet, add the action and its data to the consentMap
            // this will be sent when the gtm container has been loaded to the page
            // and this can be contingent on user consent
            const queue = this.consentMap?.get(this.CONSENT_CODE)?.queue;
            if (queue) {
                queue.push(...payloads);
            }
        }
    }
    /**
     * Performs client-defined tracking for custom tracking actions.
     *
     * This method handles custom tracking by building tracking payloads and processing
     * tracking plans for relevant actions. It supports composed action names for sub-events
     * and filters actions based on retailer ID matching.
     *
     * @param action - The event name to track
     * @param data - Additional information object for the tracking event
     * @returns void
     */
    _customTracking(action, data) {
        // build tracking payload
        const customTrkData = this._getCustomTrkData(action, data);
        // checks tracking plans for relevant actions
        if (player.data.trkActions) {
            // API-3358: clkloc events must trigger "sub-events" in tracking plan, e.g. clkloc_tel or clkloc_whatsapp.
            // The 2nd value coms from the "clkloc_type" property in data object.
            // Will also work for any event with a data prop named [action]_type.
            const composedName = `${action}_type`;
            const actions = player.data.trkActions.filter((x) => {
                return (x.code === action || (data[composedName] && x.code === `${action}_${data[composedName]}`))
                    && (!x.retailerId
                        || (x.retailerId === (customTrkData.retailer && customTrkData.retailer.id)));
            });
            if (actions.length > 0)
                this._serverTracking(actions, customTrkData);
        }
    }
    /**
     * Returns the type of delivery for the store.
     *
     * This method determines the delivery type based on the store's capabilities.
     * It checks for drive/collect options first, then brick-and-mortar, then home delivery.
     *
     * @param store - The store object containing delivery capabilities
     * @returns The delivery type as a string: 'driveOrCollect', 'brick', 'HD', or empty string
     */
    _getDlvParam(store) {
        if (store.drive || store.collect)
            return 'driveOrCollect';
        else if (store.brick)
            return 'brick';
        else if (store.homeDelivery)
            return 'HD';
        else
            return '';
    }
    /**
     * Builds tracking data payload for client-specified tracking events.
     *
     * This method constructs a comprehensive tracking payload object containing
     * product, store, retailer, and location information based on the action type.
     * It does not support the "use-old-naming-for-custom-tracking" option and
     * provides no backward-compatible tracking functionality.
     *
     * @param action - The event name to track
     * @param data - Additional information object for the tracking event
     * @returns The formatted custom tracking payload object
     */
    _getCustomTrkData(action, data = {}) {
        const trkData = {
            action: action,
            ts: Date.now(),
            wtbid: player.data.wtbid,
            fmt: 'static-player',
            type: 'awe',
            geoCountry: player.baseTrk.geocountry,
            consentId: this._getKetchId(),
            utm_source: player.baseTrk.utm_source,
            utm_campaign: player.baseTrk.utm_campaign,
            utm_term: player.baseTrk.utm_term,
            utm_content: player.baseTrk.utm_content,
            location: {
                zipCode: '',
            },
        };
        if (player.trkData && player.trkData.nbres != null)
            trkData.resultCount = player.trkData.nbres;
        if (player._store.product) {
            const pdt = player._store.product;
            trkData.product = {
                id: pdt.id,
                ean: pdt.ean,
                name: this.removeHTML(pdt.name),
                name2: this.removeHTML(pdt.name2),
                pkg: this.removeHTML(pdt.pkg),
                brand: pdt.brand,
            };
        }
        // for some actions, include store & retailer info
        if (['buy', 'clkloc', 'clkRoute', 'appointment'].includes(action) && data.avail != null) {
            trkData.store = {
                id: data.avail.store.id,
                name: data.avail.store.name,
                dlv: this._getDlvParam(data.avail.store),
                url: data.avail.store.url,
                location: {
                    city: data.avail.store.location.city,
                    zipCode: data.avail.store.location.zipCode,
                    country: data.avail.store.location.country,
                },
            };
            trkData.retailer = {
                id: data.avail.retailer.id,
                name: data.avail.retailer.name,
            };
            if (trkData.product) {
                trkData.product.retailerPdtId = data.avail.product.retailerPdtId;
                trkData.product.price = data.avail.price.amount;
            }
        }
        return trkData;
    }
    /**
     * Retrieves the Ketch consent ID from the browser's cookies.
     * @returns The consent ID from the "_swb" cookie, or an empty string if not found.
     */
    _getKetchId() {
        const cookiePairs = document.cookie.split(';');
        let consentId = '';
        for (let i = 0; i < cookiePairs.length; i++) {
            const pair = cookiePairs[i].split('=');
            if (pair[0].trim() === '_swb') {
                consentId = pair[1];
                break;
            }
        }
        return consentId;
    }
    /**
     * Handles server-defined custom tracking actions by dropping pixel URLs and inserting scripts.
     *
     * This method processes tracking actions defined on the server side, which can be either
     * pixel tag URLs to insert or scripts to execute. It checks consent status and either
     * immediately executes the tracking or queues it for later execution when consent is granted.
     *
     * @param actions - Array of tracking actions to process, each containing a value property
     * @param data - Additional data object used for placeholder replacement in tracking values
     * @returns void
     */
    _serverTracking(actions, data) {
        const trackerState = this.consentMap?.get(this.CONSENT_CODE);
        const allowed = trackerState?.accepted;
        actions.forEach(action => {
            if (typeof action.value !== 'string') {
                console.error('Server custom tracking action defined but no value, please verify configuration in database'); // eslint-disable-line no-console
                return;
            }
            const isUrl = action.value.startsWith('http');
            const type = isUrl ? 'url' : 'script';
            const value = this._replacePlaceholders(action.value, data, isUrl);
            // drops or queues tracking
            if (allowed)
                this._dropServerTracking(value, type);
            else
                trackerState?.queue?.push([value, type]);
        });
    }
    /**
     * Drops client-defined tracking, whether url or script
     * @param data - The tracking data to send (URL or script content)
     * @param type - The type of tracking data ('url' for pixel tags, 'script' for scripts)
     */
    _dropServerTracking(data, type) {
        switch (type) {
            case 'url':
                _addPixel(data);
                break;
            case 'script':
                this._addScript(data);
                break;
        }
    }
    /**
     * Loads Google Tag Manager (GTM) container into the page
     *
     * This method dynamically injects the GTM script tag into the document head.
     * In non-production environments, it uses a test GTM container to avoid diluting
     * production data and provide feedback to developers working locally.
     *
     * @param gtmId - The Google Tag Manager container ID to load
     * @returns void
     *
     * @example
     * ```typescript
     * tracker._loadGTM('GTM-XXXXXXX');
     * ```
     */
    _loadGTM(gtmId) {
        if (!gtmId || this.gtmScriptAppended)
            return;
        let container = gtmId;
        // replaces the GTMId in dev to our test container ID
        // this makes sure we are not diluting customer production data
        // AND gives feedback to devs when working locally
        if (!/^prod(uction)?$/.test(player.data.env)) {
            console.log('MikMak test GTM container has been added to the browser'); // eslint-disable-line no-console
            const mikMakTestGtmContainer = 'GTM-K4X9LPS';
            container = mikMakTestGtmContainer;
        }
        try {
            const gtmTag = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window, document,'script','dataLayer','${container}');`;
            const nd = document.createElement('script');
            nd.innerText = gtmTag;
            document.head.appendChild(nd);
            this.gtmScriptAppended = true;
        }
        catch (err) {
            logger.error('Error adding the GTM container', err);
        }
    }
    _dropLiveRampPixel() {
        const liveRampPixelId = 712896;
        const encodedPData = encodeURIComponent(`wtbid=${player.data.wtbid}`);
        _addPixel(`https://di.rlcdn.com/api/segment?pid=${liveRampPixelId}&pdata=${encodedPData}`);
        this.hasDroppedLiveRampPixel = true;
    }
    /**
     * Structures GTM event payload for version 1 (legacy) tracking events.
     *
     * This method creates backward-compatible GTM event payloads for legacy tracking
     * events. It maps specific actions to predefined GTM event names and constructs
     * the payload with product, store, and location information. This method is used
     * for maintaining compatibility with older GTM event structures.
     *
     * @param action - The event name to track
     * @param data - Additional information object for the tracking event
     * @returns The formatted GTM event payload for version 1, or undefined if no matching event
     */
    _structureGTMEventPayloadV1(action, data) {
        const gtmEvents = {
            pv: 'mikmak_load',
            buy: 'mikmak_checkout',
            variantShelfOpen: 'mikmak_variant_shelf_shown',
            clk: 'mikmak_variant_selected',
            clktab: 'mikmak_tab',
            clkloc: 'mikmak_click_offline_store',
        };
        // if the action does not have a corresponding GTM event
        // or it is the clk event generated on load
        // or it is the clkloc generated on clicking a store in offline tab
        // return early
        if (!gtmEvents[action] || (action === 'clk' && data.auto) || (action === 'clkloc' && data.clkloc_type))
            return;
        const trkData = {
            action: action,
            event: gtmEvents[action],
            ts: Date.now(),
            wtbid: player.data.wtbid,
            fmt: 'static-player',
            type: 'awe',
            geoCountry: player.baseTrk.geocountry,
            consentId: this._getKetchId(),
            location: {
                zipCode: '',
            },
        };
        if (player.trkData && player.trkData.nbres != null)
            trkData.resultCount = player.trkData.nbres;
        if (player._store.product) {
            const pdt = player._store.product;
            trkData.product = {
                id: pdt.id,
                ean: pdt.ean,
                name: this.removeHTML(pdt.name),
                name2: this.removeHTML(pdt.name2),
                pkg: this.removeHTML(pdt.pkg),
                brand: pdt.brand,
            };
        }
        // for some actions, include store & retailer info
        if (['buy', 'clkloc', 'clkRoute', 'appointment'].includes(action) && data.avail != null) {
            trkData.store = {
                id: data.avail.store.id,
                name: data.avail.store.name,
                dlv: this._getDlvParam(data.avail.store),
                url: data.avail.store.url,
                location: {
                    city: data.avail.store.location.city,
                    zipCode: data.avail.store.location.zipCode,
                    country: data.avail.store.location.country,
                },
            };
            trkData.retailer = {
                id: data.avail.retailer.id,
                name: data.avail.retailer.name,
                sanitizedName: this.sanitizeString(data.avail.retailer.name),
            };
            if (trkData.product) {
                trkData.product.retailerPdtId = data.avail.product.retailerPdtId;
                trkData.product.price = data.avail.price.amount;
            }
        }
        return trkData;
    }
    /**
     * Creates retailer list structures from availabilities
     * @param availabilities - Array of availabilities (already filtered by tab if needed)
     * @returns Object containing complete and unique retailer lists
     */
    _createRetailerLists(availabilities) {
        const completeRetailerList = [];
        const uniqueRetailers = new Map();
        availabilities.forEach((avail) => {
            const channelType = (avail.store.brick || avail.store.collect || avail.store.drive) ? 'offline' : 'online';
            const retailerData = {
                retailer_id: avail.retailer.id,
                store_id: avail.store.id,
                retailer_name: avail.retailer.id === 3364 ? 'VIP' : avail.retailer.name,
                store_name: avail.store.name,
                channel_type: channelType,
            };
            if (player.data.includeOutOfStock) {
                retailerData.in_stock = avail.product.available;
            }
            completeRetailerList.push(retailerData);
            const cacheKey = avail.store.typologyId || avail.retailer.id;
            if (!uniqueRetailers.has(cacheKey)) {
                uniqueRetailers.set(cacheKey, retailerData);
            }
        });
        return {
            completeList: completeRetailerList,
            uniqueList: Array.from(uniqueRetailers.values()),
        };
    }
    /**
     * Structures GTM event payload based on action and data.
     *
     * This method creates a Google Tag Manager event payload object that maps internal
     * tracking actions to standardized GTM event names. It supports both v1 (legacy) and v2
     * payload formats based on configuration, and includes location, product, and store
     * information when available.
     *
     * @param action - The internal tracking action name (e.g., 'clk', 'buy', 'clkloc')
     * @param data - Additional data object containing action-specific information
     * @returns An array of structured GTM event payload objects
     */
    _structureGTMEventPayload(action, data) {
        // backward compatibility GTM event
        if (!player.data.gtmEventsV2) {
            const v1Payload = this._structureGTMEventPayloadV1(action, data);
            return v1Payload ? [v1Payload] : [];
        }
        const payload = {
            wtbid: player.data.wtbid,
            geo_country: player.baseTrk.geocountry,
            consent_id: this._getKetchId(),
            commerce_type: 'media',
            experience_name: player.data.sourceName,
            account_name: player.data.accountName,
            subaccount_name: player.data.operatorName,
            utm_source: player.baseTrk.utm_source,
            utm_campaign: player.baseTrk.utm_campaign,
            utm_term: player.baseTrk.utm_term,
            utm_content: player.baseTrk.utm_content,
        };
        try {
            if (action === 'clk' && data.auto) {
                payload.event = 'mikmak_load';
            }
            else if (action === 'buy') {
                payload.event = 'mikmak_checkout';
            }
            else if (action === 'clk' && !data.auto) {
                payload.event = 'mikmak_product_selected';
            }
            else if (action === 'variantShelfOpen') {
                payload.event = 'mikmak_variant_shelf_open';
            }
            else if (action === 'clkloc') {
                if (data.clkloc_type === 'tel')
                    payload.event = 'mikmak_call_offline_store';
                else if (data.clkloc_type === 'whatsapp')
                    payload.event = 'mikmak_whatsapp_offline_store';
                else if (data.clkloc_type === 'appointment')
                    payload.event = 'mikmak_click_appointment_offline_store';
                else
                    payload.event = 'mikmak_click_offline_store'; // map
            }
            else if (action === 'geoloc') {
                payload.event = 'mikmak_update_location';
            }
            else if (action === 'view_voucher') {
                payload.event = 'mikmak_click_voucher';
            }
            else if (action === 'copy_voucher') {
                payload.event = 'mikmak_copy_voucher';
            }
            else if (action === 'clktab') {
                payload.event = 'mikmak_tab';
                payload.tab_clicked = data.tab;
            }
            if (player._store.location) {
                payload.location_zipcode = player._store.location.zipCode || '';
                payload.location_city = player._store.location.city || '';
                payload.location_state = player._store.location.region || '';
                payload.location_country = player._store.location.country || '';
            }
            // add product info
            if (player._store.product) {
                const pdt = player._store.product;
                payload.product_gtin = pdt.ean;
                payload.product_name = this.removeHTML(pdt.name);
                payload.product_brand = pdt.brand || '';
                payload.product_id = pdt.sourcePid;
                payload.product_package = this.removeHTML(pdt.pkg) || '';
                payload.product_model = this.removeHTML(pdt.model) || '';
                payload.product_line = this.removeHTML(pdt.productLine) || '';
                payload.product_range = this.removeHTML(pdt.range) || '';
                payload.product_category = this.removeHTML(pdt.catName) || '';
                if (pdt.name2) {
                    payload.product_variant = this.removeHTML(pdt.name2);
                }
            }
            // add retailer list displayed specific payload structure
            if (action === 'availabilities-update') {
                // If a tab is specified, use the template's pre-filtered lists
                // Otherwise, include all retailers (no tab context)
                let availsToUse = data.avails;
                if (data.tab) {
                    // Use the template's pre-filtered lists
                    switch (data.tab) {
                        case 'online':
                            availsToUse = player.store.onlineAvails || data.avails;
                            break;
                        case 'offline':
                            availsToUse = player.store.realAvails || data.avails;
                            break;
                    }
                }
                // Only fire the event if there are retailers to display
                if (availsToUse.length > 0) {
                    payload.event = 'mikmak_retailer_list_displayed';
                    if (data.tab)
                        payload.tab_displayed = data.tab;
                    const { completeList, uniqueList } = this._createRetailerLists(availsToUse);
                    payload.retailer_list = completeList;
                    payload.retailer_list_unique = uniqueList;
                }
                else {
                    // No retailers to display, don't fire the event
                    return [];
                }
            }
            // add purchase or click specific info
            if (action === 'buy' || action === 'clkloc') {
                payload.product_price = data.avail.price.amount;
                payload.product_price_currency = data.avail.price.currency;
                payload.product_price_text = i18n.formatPrice(data.avail.price.amount, data.avail.price.currency);
                payload.retailer_id = data.avail.retailer.id;
                payload.retailer_name = data.avail.retailer.name;
                payload.retailer_name_sanitized = this.sanitizeString(data.avail.retailer.name);
                payload.store_id = data.avail.store.id;
                payload.store_name = data.avail.store.name;
                payload.store_city = data.avail.store.location.city;
                payload.store_zipcode = data.avail.store.location.zipCode;
            }
        }
        catch (e) {
            console.error('Error structuring GTM event payload', e); // eslint-disable-line no-console
        }
        // If no event was set, this action doesn't have a GTM mapping - return early
        if (!payload.event) {
            return [];
        }
        // For clktab, we need to track 2 separate events: tab_clicked and retailer_list
        if (action === 'clktab') {
            const payloads = [payload]; // First payload: mikmak_tab with tab_clicked
            // Create second payload for retailer_list
            let availsToUse = data.avails;
            if (data.tab) {
                switch (data.tab) {
                    case 'online':
                        availsToUse = player.store.onlineAvails || data.avails;
                        break;
                    case 'offline':
                        availsToUse = player.store.realAvails || data.avails;
                        break;
                }
            }
            if (availsToUse.length > 0) {
                const { completeList, uniqueList } = this._createRetailerLists(availsToUse);
                const retailerListPayload = { ...payload };
                retailerListPayload.event = 'mikmak_retailer_list_displayed';
                retailerListPayload.retailer_list = completeList;
                retailerListPayload.retailer_list_unique = uniqueList;
                if (data.tab)
                    retailerListPayload.tab_displayed = data.tab;
                delete retailerListPayload.tab_clicked;
                payloads.push(retailerListPayload);
            }
            return payloads;
        }
        return [payload];
    }
    /**
     * Sends a GTM event to the data layer.
     *
     * This method pushes the provided event payload to the Google Tag Manager data layer.
     * For 'buy' actions, it also sends an additional 'mikmak_retailer_preference' event
     * with the same payload data to track retailer preferences.
     *
     * @param eventPayload - The GTM event payload to send to the data layer
     * @returns void
     */
    _sendGTMEvent(eventPayload) {
        if (!window.dataLayer)
            return;
        window.dataLayer.push(eventPayload);
        // also send the 'mikmak_retailer_preference' event if the action is `buy`
        if ('action' in eventPayload && eventPayload.action === 'buy') {
            // also send retailer preference universal event with the same payload
            // make a copy of the payload because when the buy event is queued
            // and sent after user consent, for some reason the 'mikmak_retailer_preference' event
            // is sent twice and the buy event is never sent
            // this way there is clear differentiation between the two events/payloads
            const retailPrefPayload = {
                ...eventPayload,
                event: 'mikmak_retailer_preference',
            };
            window.dataLayer.push(retailPrefPayload);
        }
    }
    /**
     * Processes a string (URL or script) to replace placeholders with actual values.
     *
     * This method replaces placeholders delimited by double-brackets (e.g., {{property.path}})
     * with corresponding values from the provided data object. It supports nested property
     * paths and handles encoding when specified.
     *
     * @param str - The string containing placeholders to be replaced
     * @param data - The data object containing values to substitute for placeholders
     * @param encode - Whether to URL-encode the substituted values
     * @returns The processed string with placeholders replaced by actual values
     */
    _replacePlaceholders(str, data, encode) {
        if (data == null) // no data => nothing to replace
            return str;
        // new name scheme: properties are not unique. placeholder is a path to the value (e.g. '{{store.location.country}}').
        // We decompose each placeholder into a list of keys and mine the data object key after key until we've processed to whole path.
        str = str.replace(/{{(.+?)}}/g, (match, pattern) => {
            const keys = pattern.split('.');
            let value = data;
            try {
                for (let i = 0; i < keys.length; i++) {
                    value = value[keys[i]];
                }
                if (value == null)
                    return '';
                else
                    return encode ? encodeURIComponent(value.toString()) : value.toString();
            }
            catch (ex) { // on error (e.g. accessing a null object), return the placeholder
                return match;
            }
        });
        return str;
    }
    /**
     * Adds tracking script to the document, supporting both pure JavaScript and HTML formats.
     *
     * This method dynamically injects tracking scripts into the document body. It can handle
     * both pure JavaScript code and HTML containing multiple script tags. For HTML scripts,
     * it processes each script tag sequentially, waiting for external scripts to load before
     * proceeding to the next one. Pure JavaScript is wrapped in a try-catch block for error handling.
     *
     * @param script - The tracking script content, either pure JavaScript or HTML with script tags
     * @returns void
     */
    _addScript(script) {
        // whether script is js or html - just look at a script closing tag.
        // NOTE: we use the unicode codepoint for the opening less-than sign to not interfere
        //       with media pops created from parsed response.
        const isHTML = script.indexOf('\u003C/script>') > -1;
        if (isHTML) {
            // split string into each script
            const scripts = script.match(/<script.*?<\/script>/gs);
            if (scripts && scripts.length >= 1) {
                scripts.reduce(async (prom, script, idx, list) => {
                    const openingTag = script.match(/<script.*?>/)?.[0], // extract the opening tag
                    hasExternal = openingTag?.match(/src=['"](.+?)['"]/), // whether opening tag contains an src attribute
                    nd = document.createElement('script');
                    if (hasExternal)
                        nd.src = hasExternal[1];
                    else
                        nd.innerHTML = script.replace(/<script.*?>/, '').replace('\u003C/script>', '');
                    let p;
                    // if script has an external src, wait for it to load before inserting next script.
                    // Only for scripts that are not the last one.
                    if (hasExternal && idx < list.length - 1) {
                        p = new Promise((resolve, reject) => {
                            nd.addEventListener('load', resolve);
                            nd.addEventListener('error', function () {
                                reject('Load error for script ' + nd.src);
                            });
                        });
                    }
                    else {
                        p = Promise.resolve();
                    }
                    await prom;
                    document.body.appendChild(nd);
                    return p;
                }, Promise.resolve());
            }
        }
        else {
            // script is pure js, wrap in try/catch and insert.
            const nd = document.createElement('script');
            nd.innerHTML = 'try{' + script + '}catch(err){console.error(err)}';
            document.body.appendChild(nd);
        }
    }
    /**
     * Creates a map of consent purposes for tracking components.
     *
     * This method builds a consent map that tracks the acceptance status and queues
     * for various tracking components including Ketch consent purposes, GTM events,
     * and LiveRamp pixels. The map is used to manage consent-based tracking and
     * queue events until consent is granted.
     *
     * @param actions - Array of tracking actions from the tracking plan
     * @param gtmIdPresent - Flag indicating whether a Google Tag Manager ID is configured
     * @param isLiveRampEnabled - Flag indicating whether LiveRamp pixel tracking is enabled
     * @returns A ConsentMap with tracking component states, or undefined if no tracking is configured
     */
    _buildConsentMap(actions, gtmIdPresent, isLiveRampEnabled) {
        const consentMap = new Map();
        if ((actions.length > 0
            || gtmIdPresent
            || isLiveRampEnabled)
            && !consentMap.has(this.CONSENT_CODE)) {
            consentMap.set(this.CONSENT_CODE, {
                accepted: null, // trackers are blocked by default, until Ketch tells us it's ok
                queue: [], // store blocked trackings, to replay later if tracker is accepted.
            });
        }
        if (consentMap.size)
            return consentMap;
        else
            return undefined;
    }
    /**
     * Removes HTML tags and normalizes whitespace from a string.
     *
     * This method cleans HTML content by removing all HTML tags, converting line breaks
     * to spaces, and normalizing multiple consecutive spaces into single spaces.
     * It's commonly used to sanitize product names and descriptions for tracking events.
     *
     * @param input - The string containing HTML markup to be cleaned
     * @returns The cleaned string with HTML tags removed and whitespace normalized
     */
    removeHTML(input) {
        let res = input;
        if (typeof input === 'string') {
            res = input.replace(/<br\s*\/?>/gi, ' ') // replace line breaks with spaces
                .replace(/<[^>]*>/g, '') // remove all html tags
                .replace(/\s{2,}/g, ' ').trim(); // collapse multiple spaces
        }
        return res;
    }
    /**
     * Sanitizes a string by removing diacritics and normalizing special characters.
     *
     * This method processes input strings to create normalized versions
     * by removing diacritical marks (accents), converting spaces and special
     * characters to underscores. This is commonly
     * used for creating sanitized identifiers from user-facing text.
     * @see https://en.wikipedia.org/wiki/Unicode_equivalence#Normalization
     * @param input - The string to be sanitized
     * @returns The sanitized string with diacritics removed and special characters normalized to underscores
     */
    sanitizeString(input) {
        // remove diacritics
        // replace spaces and special characters not '-' by '_'
        return input?.normalize('NFD') // normalize diacritics
            .replace(/[\u0300-\u036f]/g, '') // remove diacritics
            .replaceAll(/[^\w-]/gi, '_') // replace spaces and special characters not '-' by '_'
            .toLowerCase();
    }
}
window.tracker = new Tracker();
tracker.track('pv', { auto: true }, TARGET_ALL);
/**
 * Controls MikMak event consent for tracking and analytics.
 *
 * This function allows or disallows MikMak event consent. It must be called by whatever consent management
 * is used when Ketck is disabled.
 * Consent determines whether tracking events and analytics data can be collected and processed.
 * When consent is granted, tracking events are sent to analytics services.
 * When consent is denied, tracking events are blocked or queued until consent is provided.
 *
 * @param accepted - Boolean flag indicating whether consent is accepted (true) or denied (false)
 * @returns void
 */
function mikmakConsent(accepted) {
    window.tracker.setConsent(accepted, false);
}
/**
 * Logger class providing event and error logging functionality as a wrapper around the Tracker.
 *
 * This class is responsible for logging events, errors, and debug information, and forwarding
 * them to the underlying Tracker for integration with analytics and monitoring services (such as Datadog).
 *
 * Methods in the Logger class ensure events are marked appropriately for logging and output them
 * to the console based on environment and log level.
 *
 * @example
 * // Log an info event
 * logger.log('custom-event', { foo: 1 });
 *
 * @example
 * // Log an error
 * logger.error('api-error', new Error('Failed request'));
 */
class Logger {
    constructor(env) {
        this.env = env;
    }
    /**
     * Internal method to track events with logging flag.
     *
     * This private method adds a logging flag to the tracking data and forwards
     * the event to the tracker with datadog target (bitmask 2). It's used by
     * the Logger class to ensure all logged events are properly marked and
     * sent to the appropriate logging service.
     *
     * @param action - The event action name to track
     * @param data - Additional data object for the tracking event
     * @returns void
     */
    _track(action, data) {
        data.log = true;
        tracker.track(action, data, 2);
    }
    /**
     * Logs an event with specified action and data.
     *
     * This method logs events with optional data and automatically sets a default
     * log level of 'info' if none is provided. The event is tracked internally
     * and may be output to console depending on the environment and log level.
     *
     * @param action - The event action name to log
     * @param data - Additional data object for the log event (optional)
     * @returns void
     */
    log(action, data) {
        data = data || {};
        if (!data.level)
            data.level = 'info';
        this._track(action, data);
        // no console output for info outside dev
        if (this.env === 'dev' || data.level !== 'info')
            console.log(action, data); // eslint-disable-line no-console
    }
    /**
     * Logs an error with additional message context.
     *
     * This method logs error events by appending the provided message to any existing
     * error message, setting the log level to 'error', and tracking the error
     * through the internal tracking system. The error is also output to console.
     *
     * @param msg - Additional message to append to the error message
     * @param err - The error object to log
     * @returns void
     */
    error(msg, err) {
        err = err || {};
        if (msg)
            err.message += ': ' + msg;
        err.level = 'error';
        this._track('error', err);
        console.error(err); // eslint-disable-line no-console
    }
    /**
     * Logs user actions without console output.
     *
     * This method tracks user actions such as page views and store displays
     * for analytics purposes without generating console output. The event
     * is logged internally with an 'info' level for tracking purposes.
     *
     * @param action - The user action to log (e.g., 'pv', 'storeDisplayed')
     * @param data - Additional data object for the event (optional)
     * @returns void
     */
    event(action, data) {
        data = data || {};
        data.level = 'info';
        this._track(action, data);
    }
}
window.logger = new Logger(player.data.env);
